import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import sortedArrayRemoveDuplicates from "./sortedArrayRemoveDuplicates";
import createCsvURL from "./convertToCSV";
import { MultiSelect } from "../../lib/iprs-react-library/src/index.js";

const TableChargeableView = ({api, clinicians, events, setError}) => {

	const clinicianIDs =
		sortedArrayRemoveDuplicates(events.flatMap(e => {
			const clinIDs = [...(e.ClinicianPartyIDs) ?? [], e.PartyID ?? 0];
			return clinIDs;
		}).toSorted((a, b) => a - b))
			.filter(v => 0 !== v);

	const cliniciansInSlots =
		clinicians.filter(c => clinicianIDs.includes(c.ClinicianPartyID))
			.map(c => ({
				name: c.FirstName + ' ' + c.LastName,
				ClinicianPartyID: c.ClinicianPartyID
			}));

	const columns = [
		{
			name: 'Start Date',
			selector:	row => row.startDate,
			sortable: true,
			minWidth: '5em',
			maxWidth: '15em'
		},
		{
			name: 'Start Time',
			selector:	row => row.startTime,
			sortable: true,
			minWidth: '5em',
			maxWidth: '15em'
		},
		{
			name: 'End Date',
			selector:	row => row.endDate,
			sortable: true,
			minWidth: '5em',
			maxWidth: '15em'
		},
		{
			name: 'End Time',
			selector:	row => row.endTime,
			sortable: true,
			minWidth: '5em',
			maxWidth: '15em'
		},
		{
			name: 'Clinician',
			selector:	row => row.clinicianFullName?? row.name,
			sortable: true,
			minWidth: '5em',
			maxWidth: '15em'
		},
		{
			name: 'Event',
			selector:	row => row.title?? JSON.stringify(row),
			sortable: true,
			minWidth: '5em',
			maxWidth: '50em'
		},
		{
			name: 'Total Minutes',
			selector:	row => row.totalMinutes,
			sortable: true,
			minWidth: '5em',
			maxWidth: '50em'
		},
		{
			name: 'Hours',
			selector:	row => row.hours,
			sortable: true,
			minWidth: '5em',
			maxWidth: '50em'
		},
		{
			name: 'Minutes',
			selector:	row => row.minutes,
			sortable: true,
			minWidth: '5em',
			maxWidth: '50em'
		}
	];

	const [selectedItems, setSelectedItems]	=	useState([]);
	const [rotaTypeList, setRotaTypeList] 	=	useState([]);
	useEffect(() => {
		api.transact( 'GetRotaTypeList', {} )
		.then(r => r.apiResult)
		.then(r => r.filter(rt => 1 !== rt.RotaTypeID))
		.then(r => setRotaTypeList(r))
		.catch(e => setError(e.message));
	}, [api, setError, setRotaTypeList]);

	const eventIDFunPartyTogether = event => (''+event.PartyID+event.type+event.StartTime);
	const eventIDFunTimeOrder = event => (''+event.StartTime+event.PartyID+event.type);
	const eventComparator = orderFun => (a,b) => (orderFun(a)).localeCompare(orderFun(b));
	const filteredSortedWithDurationCalculatedEvents = events
		.filter(e => {
			if(0 === e.ClinicianCount){
				return false; // don't include unavailable slots
			}else if ('task' === e.type && !selectedItems.includes(e.RotaTypeID)) {
				return false;
			}else{
				return true;
			}
		}).map(e => {
			// expand the availability to a separate record for each clinician that it marks as available
			if('availability' === e.type){
				return cliniciansInSlots
					.filter(c => e.ClinicianPartyIDs.includes(c.ClinicianPartyID))
					.map(c => ({...c, ...e, PartyID: c.ClinicianPartyID}));
			}else{
				return [e];
			}
		}).flat()
		.toSorted(eventComparator(eventIDFunPartyTogether)) // to detect adjoins, need each party all together
		.reduce((prev, curr) => {
			/* Here we adjoin any events that start at the same time 
			as the previous finishes */
			if(0 < prev.length){
				const lastIndex = prev.length - 1;
				const prevItem = prev[lastIndex];
				const eventIDRoot = event => ''+event.PartyID+event.type;
				// if two events are adjoined, then join them
				if((eventIDRoot(prevItem) + prevItem.FinishTime) === (eventIDRoot(curr) + curr.StartTime)) {
					const newItem = {...prevItem, FinishTime: curr.FinishTime, end: curr.FinishTime, adjoined: true};
					const newArray = prev.toSpliced(lastIndex, 1, newItem);

					return newArray;
				}
			}
			return [...prev, curr]
		}, [])
		.map(e => eventDateSubtract(e))
		.toSorted(eventComparator(eventIDFunTimeOrder)); // we disply in time order
	
    return <>
		<div style={{ float: 'left', clear: 'right' }}>
                <label htmlFor="selectTaskType">Select Task Types</label>
                <MultiSelect selectableItems={rotaTypeList} 
                        selectedItems={selectedItems}
                        setSelectedItems={setSelectedItems}
                        getItemID={taskType=>taskType.RotaTypeID}
						getItemTitle={taskType=>taskType.Description}
                        defaultItemID={0}
                        name={'selectTaskType'} />
            </div>
		{/* <pre>{JSON.stringify(selectedItems, null, '\t')}</pre> */}
		<div style={{marginBottom: '3em'}}>
        	<DataTable columns={columns} data={filteredSortedWithDurationCalculatedEvents} fixedHeader={true} fixedHeaderScrollHeight={'80ex'} /> 
		</div>
		<a href={createCsvURL(filteredSortedWithDurationCalculatedEvents, columns)} download={'export.csv'}  >Download as CSV</a>
    </>
}

// use substring to remove the time from the date time
const dateTimeToDate = dateTime => dateTime.substring(0, 10);

const getTimeFromDate = date => date.substring(11, 16);

const eventDateSubtract = event => {

	const { start, end } = event;
	const totalMinutes = (Date.parse(end) - Date.parse(start)) / 60000;
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	const duration = hours + ':' + minutes;

	const startDate = dateTimeToDate(start);
	const endDate = dateTimeToDate(end);

	const startTime = getTimeFromDate(start);
	const endTime = getTimeFromDate(end);

	const result = {...event, minutes, totalMinutes, startDate, endDate, startTime, endTime, duration, hours};
	return result;
}

export default TableChargeableView;